import { computed, type Ref } from 'vue'
import { useFormatting } from '@gat-ship/utils'
import { isEtaFieldComplete, type EtaFieldValue } from '../model/eta-bridge'
import type { ArrivalRequirementState } from '../model/portcall-wizard'
import {
  type ArrivalStepKey,
  type PortcallStep
} from '../components/providers/create-portcall-provider/step-flow/create-portcall-steps'
import type { SidebarStep } from '../components/providers/create-portcall-provider/step-flow/PortcallStepSidebar.vue'
import type { CreatePortcallFormValues } from './useCreatePortcallForm'
import type { usePortcallWizard } from './usePortcallWizard'

/** The arrival-step heading questions (presentation copy, keyed by field). */
const ARRIVAL_TITLES: Record<ArrivalStepKey, string> = {
  etaAtPilotStation: 'When is it expected at the pilot station?',
  etaAtPort: 'When is it expected at the port?'
}

/** The eyebrow label above each arrival step's heading. */
const ARRIVAL_EYEBROWS: Record<ArrivalStepKey, string> = {
  etaAtPilotStation: 'ETAPS',
  etaAtPort: 'ETA'
}

/**
 * Requirement-banner copy per (arrival step, state). The *state* decision is
 * the pure `arrivalRequirementState` in the model; only this copy is
 * presentation. Pilot never reaches `required`; port covers all three.
 */
const ARRIVAL_COPY: Record<
  'pilot' | 'port',
  Partial<Record<ArrivalRequirementState, { title: string; sub: string }>>
> = {
  pilot: {
    met: {
      title: 'Pilot station arrival set',
      sub: 'You can change this or continue to the port arrival.'
    },
    optional: {
      title: 'Pilot station arrival — optional',
      sub: 'Add a date & time, or skip this step. If you skip it, the port arrival becomes required.'
    }
  },
  port: {
    met: {
      title: 'Arrival time set',
      sub: 'You can change this or continue to review and create the call.'
    },
    optional: {
      title: 'Port arrival — optional',
      sub: 'The pilot-station arrival is set, so you can continue to review now — or add the port arrival too.'
    },
    required: {
      title: 'Arrival date required',
      sub: 'Set the expected arrival at the port — this is required to create the call.'
    }
  }
}

/** The requirement banner shown on the active arrival step. */
interface ArrivalRequirement {
  state: ArrivalRequirementState
  title: string
  sub: string
}

export interface StepFlowViewInputs {
  /** The wizard seam (visible steps, gating, completeness, requirement state). */
  wizard: ReturnType<typeof usePortcallWizard>
  /** The live form values. */
  formValues: Readonly<Ref<CreatePortcallFormValues>>
  /** Display labels captured when a list option is chosen. */
  selectedLabels: Readonly<Ref<Record<string, string>>>
  /** The step currently being edited. */
  currentStepKey: Readonly<Ref<PortcallStep['key']>>
  /** Whether the success screen is showing. */
  done: Readonly<Ref<boolean>>
  /** The server-assigned call number, once the call is created. */
  createdCallNumber: Readonly<Ref<string | null>>
  /** Whether the selected port captures a separate pilot-station ETA. */
  pilotStationActive: Readonly<Ref<boolean>>
  /** Whether the port's pilot-station flag is still resolving. */
  loadingLocalPort: Readonly<Ref<boolean>>
}

/**
 * The presentation view-model for the create-portcall step flow: the sidebar
 * step list, the footer hints/labels/gating, and the active arrival step's
 * heading + requirement banner. The orchestrator binds these; it doesn't
 * compute them. All wizard *decisions* come from {@link usePortcallWizard}; this
 * layer only formats them for display.
 */
export function usePortcallStepFlowView(inputs: StepFlowViewInputs) {
  const {
    wizard,
    formValues,
    selectedLabels,
    currentStepKey,
    done,
    createdCallNumber,
    pilotStationActive,
    loadingLocalPort
  } = inputs
  const {
    visibleSteps,
    currentStep,
    currentIndex,
    isLastStep,
    isStepComplete,
    nextVisible,
    canEnter,
    hasValueForStep,
    arrivalRequirementState
  } = wizard
  const { formatDate } = useFormatting()

  function arrivalValue(step: PortcallStep): EtaFieldValue {
    return formValues.value[step.key as ArrivalStepKey]
  }

  function formatArrival(eta: EtaFieldValue): string {
    if (!eta?.date) return ''
    const day = formatDate(eta.date)
    if (eta.precision === 'exact-time' && eta.time)
      return `${day} · ${eta.time}`
    if (eta.precision === 'named-time' && eta.name)
      return `${day} · ${eta.name}`
    return day
  }

  /** The sidebar value under a step's label: a list label or a formatted ETA. */
  function displayValue(step: PortcallStep): string {
    if (step.kind === 'list') return selectedLabels.value[step.key] ?? ''
    const eta = arrivalValue(step)
    return isEtaFieldComplete(eta) ? formatArrival(eta) : ''
  }

  function lockTextFor(step: PortcallStep): string {
    if (step.key === 'portOfCallLocationCode') return 'Select an agency first'
    if (step.key === 'etaAtPilotStation' || step.key === 'etaAtPort')
      return 'Select a port first'
    return ''
  }

  /**
   * Whether a step is optional right now. The pilot-station arrival is always
   * optional; the port arrival becomes optional once a pilot-station ETA has
   * been provided (then it already satisfies the call).
   */
  function stepOptional(step: PortcallStep): boolean {
    if (step.optional) return true
    if (step.key === 'etaAtPort')
      return (
        pilotStationActive.value &&
        isEtaFieldComplete(formValues.value.etaAtPilotStation)
      )
    return false
  }

  const sidebarSteps = computed<SidebarStep[]>(() =>
    visibleSteps.value.map((step) => ({
      key: step.key,
      group: step.group,
      label: step.label,
      // The summary step ticks once the call is created, not from a field.
      hasValue: step.kind === 'summary' ? done.value : hasValueForStep(step),
      current: step.key === currentStepKey.value,
      locked: !canEnter(step),
      optional: stepOptional(step),
      value: displayValue(step),
      lockText: lockTextFor(step)
    }))
  )

  // --- Post-create sidebar ---
  // Once the call is created the creation steps collapse away and the rail
  // shows the created port call as its new root — the anchor for any future
  // post-create steps run inside this modal.
  const createdLabel = computed(() =>
    createdCallNumber.value
      ? `Portcall ${createdCallNumber.value}`
      : 'Portcall created'
  )
  const createdSidebar = computed(() =>
    done.value ? { label: createdLabel.value } : null
  )

  /** The recap rows shown on the summary step (every answer but the summary). */
  const summaryRows = computed(() =>
    visibleSteps.value
      .filter((step) => step.kind !== 'summary')
      .map((step) => ({
        key: step.key,
        label: step.label,
        value: displayValue(step)
      }))
  )

  // --- Active arrival step ---
  const arrivalTitle = computed(
    () => ARRIVAL_TITLES[currentStep.value.key as ArrivalStepKey] ?? ''
  )
  const arrivalEyebrow = computed(
    () => ARRIVAL_EYEBROWS[currentStep.value.key as ArrivalStepKey] ?? ''
  )
  /** Whether the active arrival step is optional (tags an "(optional)" suffix). */
  const arrivalOptional = computed(() => stepOptional(currentStep.value))
  const arrivalRequirement = computed<ArrivalRequirement>(() => {
    const step = currentStep.value
    if (step.kind !== 'arrival')
      return { state: 'required', title: '', sub: '' }
    const state = arrivalRequirementState(step)
    const isPilot = step.key === 'etaAtPilotStation'
    const copy = (isPilot ? ARRIVAL_COPY.pilot : ARRIVAL_COPY.port)[state]
    return { state, title: copy?.title ?? '', sub: copy?.sub ?? '' }
  })
  // On a pilot-station port the banner is now only worth showing while the
  // port arrival is still required: "met" is self-evident from the filled
  // field and "optional" lives in the step header. Single-ETA ports keep
  // their prior behaviour (no banner — the field + disabled Continue suffice).
  const showArrivalRequirement = computed(
    () =>
      pilotStationActive.value &&
      currentStep.value.key === 'etaAtPort' &&
      arrivalRequirement.value.state === 'required'
  )

  // --- Footer ---
  const isList = computed(() => currentStep.value.kind === 'list')
  const isArrival = computed(() => currentStep.value.kind === 'arrival')
  const isSummary = computed(() => currentStep.value.kind === 'summary')
  const pilotUnset = computed(
    () =>
      currentStep.value.key === 'etaAtPilotStation' &&
      !isEtaFieldComplete(formValues.value.etaAtPilotStation)
  )
  // The vessel step is skippable with no selection — skipping assigns the "to
  // be named" (TBN) sentinel. Drives the "Skip (TBN)" footer label + gating.
  const vesselUnset = computed(
    () => currentStep.value.key === 'vesselImo' && !formValues.value.vesselImo
  )

  const progressText = computed(
    () => `Step ${currentIndex.value + 1} of ${visibleSteps.value.length}`
  )
  const enterHint = computed(() => {
    if (isList.value) return 'select'
    if (pilotUnset.value) return 'skip'
    return isLastStep(currentStep.value) ? 'create' : 'continue'
  })
  const nextLabel = computed(() => {
    if (vesselUnset.value) return 'Skip (TBN)'
    if (pilotUnset.value) return 'Skip'
    return isLastStep(currentStep.value) ? 'Create Port Call' : 'Continue'
  })
  const canNext = computed(() => {
    const step = currentStep.value
    // The vessel step can always advance — skipping assigns TBN (see goNext).
    if (vesselUnset.value) return true
    if (!isStepComplete(step)) return false
    // Port step: hold Continue until the pilot-station flag has resolved so
    // the next visible step is computed correctly.
    if (step.key === 'portOfCallLocationCode' && loadingLocalPort.value)
      return false
    const next = nextVisible()
    return !next || canEnter(next)
  })

  return {
    sidebarSteps,
    createdSidebar,
    summaryRows,
    arrivalTitle,
    arrivalEyebrow,
    arrivalOptional,
    arrivalRequirement,
    showArrivalRequirement,
    isList,
    isArrival,
    isSummary,
    pilotUnset,
    vesselUnset,
    progressText,
    enterHint,
    nextLabel,
    canNext
  }
}
